export interface UserBadge {
  id: string;
  userId: string;
  badgeType: string;
  createdAt: string;
}

export interface Badge {
  title: string;
  description: string;
  badgeType: string;
  imageUrl: string;
  isEarned?: boolean;
}

// Storage key for tracking already-seen badges in this browser session
export const SEEN_BADGES_STORAGE_KEY = 'interview-academy-seen-badge-ids';

// Last session timestamp to track app sessions
export const LAST_SESSION_TIMESTAMP_KEY = 'interview-academy-last-session';

/**
 * Updates the session timestamp to mark the current user session
 */
export function updateSessionTimestamp(): void {
  try {
    localStorage.setItem(LAST_SESSION_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error saving session timestamp:', error);
  }
}

/**
 * Checks if this is a new session for the user
 * A new session is defined as being more than 30 minutes since the last session
 */
export function isNewSession(): boolean {
  try {
    const lastSessionTime = localStorage.getItem(LAST_SESSION_TIMESTAMP_KEY);
    if (!lastSessionTime) return true;

    const lastTime = parseInt(lastSessionTime, 10);
    const currentTime = Date.now();
    // Consider a new session if it's been more than 30 minutes
    const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

    return currentTime - lastTime > SESSION_TIMEOUT_MS;
  } catch (error) {
    console.error('Error checking session status:', error);
    return true;
  }
}

/**
 * Get badge IDs that have been shown to the user in this browser
 */
export function getSeenBadgeIds(): string[] {
  try {
    const storedSeenBadges = localStorage.getItem(SEEN_BADGES_STORAGE_KEY);
    return storedSeenBadges ? JSON.parse(storedSeenBadges) : [];
  } catch (error) {
    console.error('Error retrieving seen badge IDs from storage:', error);
    return [];
  }
}

/**
 * Mark a badge as seen by the user in this browser using its ID
 */
export function markBadgeAsSeen(badgeId: string): void {
  try {
    const seenBadgeIds = getSeenBadgeIds();
    if (!seenBadgeIds.includes(badgeId)) {
      localStorage.setItem(
        SEEN_BADGES_STORAGE_KEY,
        JSON.stringify([...seenBadgeIds, badgeId])
      );
    }
  } catch (error) {
    console.error('Error saving seen badge ID to storage:', error);
  }
}

/**
 * Utility to determine if a badge is newly earned in the current session
 * @param badge The badge to check
 * @param userBadges User badges from the API
 * @param isNewAppSession Whether this is a new app session
 * @returns Whether the badge should be shown as new to the user
 */
export function isNewlyEarnedBadge(
  badge: Badge,
  userBadges: UserBadge[],
  isNewAppSession: boolean
): boolean {
  // If the badge isn't earned at all, it can't be newly earned
  if (!badge.isEarned) return false;

  // Find the matching user badge
  const matchingUserBadge = userBadges.find(
    userBadge => userBadge.badgeType === badge.badgeType
  );

  // If no matching badge, it shouldn't be possible if isEarned is true
  if (!matchingUserBadge) return false;

  // Use the badge ID for uniqueness - this allows users who reset progress to see badges again
  const badgeId = matchingUserBadge.id;

  // If this is not a new session, we check local storage to avoid showing repeat notifications
  if (!isNewAppSession) {
    // If it's already been seen in this browser, don't show it again
    const seenBadgeIds = getSeenBadgeIds();
    if (seenBadgeIds.includes(badgeId)) return false;
  }

  // For a possibly new badge, check if it was earned in the last 5 minutes
  // If the badge was earned more than 5 minutes ago, consider it "old news"
  // and don't show a notification for it in a new browser/session
  const badgeDate = new Date(matchingUserBadge.createdAt);
  const fiveMinutesInMs = 5 * 60 * 1000;
  const isRecentlyEarned = Date.now() - badgeDate.getTime() < fiveMinutesInMs;

  return isRecentlyEarned;
}

export const academyBadges: Badge[] = [
  {
    title: 'Ahead of the Pack',
    description: 'Started Academy',
    badgeType: 'first-substep-complete',
    imageUrl:
      'https://res.cloudinary.com/oblaka/image/upload/v1740603914/badge-01_uvfjqq.png',
  },
  {
    title: 'Interview Apprentice',
    description: 'Foundations',
    badgeType: 'first-level-complete',
    imageUrl:
      'https://res.cloudinary.com/oblaka/image/upload/v1740603914/badge-02_vskyqq.png',
  },
  {
    title: 'Interview Explorer',
    description: 'Common Questions',
    badgeType: 'second-level-complete',
    imageUrl:
      'https://res.cloudinary.com/oblaka/image/upload/v1740603914/badge-01_uvfjqq.png',
  },
  {
    title: 'Behavioral Apprentice',
    description: 'Behavioral Questions',
    badgeType: 'third-level-complete',
    imageUrl:
      'https://res.cloudinary.com/oblaka/image/upload/v1740603914/badge-02_vskyqq.png',
  },
  {
    title: 'Strategic Apprentice',
    description: 'Hypothetical Questions',
    badgeType: 'fourth-level-complete',
    imageUrl:
      'https://res.cloudinary.com/oblaka/image/upload/v1740603914/badge-01_uvfjqq.png',
  },
  {
    title: 'Interview Challenger',
    description: 'Academy Complete',
    badgeType: 'fifth-level-complete',
    imageUrl:
      'https://res.cloudinary.com/oblaka/image/upload/v1740603914/badge-02_vskyqq.png',
  },
];
